import type { CallDumpTurn } from "@voicely/zod-schemas/call_dump.zod";

/**
 * Where one turn's latency went — the operator's view.
 *
 * This used to be drawn in the workspace app, under every agent turn. It
 * moved here because it names our VAD and its scores, our utterance-hold
 * setting and each vendor's per-stage timing, and a customer can act on none
 * of it. The workspace keeps the caller's wait and its own feedback; the
 * tenant API no longer sends the marks this is computed from.
 *
 * ## Why a stage can be missing rather than zero
 *
 * The runtime records a mark only when it is actually reached, encodes an
 * absent one as `0` on the wire, and the control plane decodes that back to
 * null. A turn cut short by a barge-in genuinely never reached most of them.
 * So a missing stage renders as `—`, never as `0ms`: a missing measurement and
 * a fast one are different facts, and showing them the same way is how a
 * percentile ends up flattering.
 */

/**
 * A stage is a span between two marks. Either end missing means unknown.
 *
 * So does a negative one. Marks are stamped by different processes and a
 * clock or an ordering bug can put the end before the start; clamping that to
 * `0` would report the impossible as the ideal — the "fast measured zero"
 * this module's header explains is the wrong answer — so it is `null`, drawn
 * as `—`, and `stageOrderViolated` says why.
 */
export function span(from: number | null, to: number | null): number | null {
	if (from === null || to === null) return null;
	if (to < from) return null;
	return to - from;
}

/** Whether a span was dropped because its marks ran backwards. */
export function stageOrderViolated(from: number | null, to: number | null): boolean {
	return from !== null && to !== null && to < from;
}

/**
 * An agent turn that had words and played nothing.
 *
 * A synthesis vendor can answer 200, report success and return nothing usable
 * — which produced a clean transcript, a settled session, green health checks
 * and a caller hearing silence. It has happened twice and both times a human
 * listening found it.
 *
 * `replyAudioMs === null` is excluded on purpose: that is a turn nobody
 * measured, not a turn that produced nothing. `interrupted` is excluded too —
 * a barge-in cuts playback short deliberately, and one that fired before the
 * first chunk legitimately played nothing.
 */
export function silentReply(turn: CallDumpTurn): boolean {
	return (
		turn.speaker === "agent" &&
		// `text` + `unspokenText`, not `text`. The runtime moves unheard words
		// into `unspokenText`, and a turn that produced no audio has nothing
		// spoken by construction — so reading `text` alone would switch this
		// check off for exactly the failure it exists to catch.
		[turn.text, turn.unspokenText ?? ""].join("").trim() !== "" &&
		turn.replyAudioMs === 0 &&
		!turn.interrupted
	);
}

/**
 * Why the reply was cancelled, in one line a reviewer can act on.
 *
 * The distinction it exists to make is `agentWasPlaying`. A turn cancelled
 * while audio was on the wire is an interruption — the caller talked over the
 * agent, which is the feature working. A turn cancelled before synthesis
 * delivered anything is the agent **saying nothing at all**, and a caller
 * experiences that as being ignored rather than as being interrupted.
 *
 * The score is shown with its detector because it is in that detector's own
 * unit — a probability for Silero, a mean square for Energy — and a bare
 * number invites comparing two quantities that share no scale.
 */
export function interruptSummary(turn: CallDumpTurn): string | null {
	const evidence = turn.interrupt;

	// Interrupted with nothing recorded: every turn from before the runtime
	// carried its evidence. Said out loud, because silence here would read as
	// "no reason was needed".
	if (!evidence) {
		return turn.interrupted ? "Cancelled, but this call predates the runtime recording why." : null;
	}

	if (evidence.trigger === "manual") {
		return "Cancelled by an explicit barge-in call — an operator or a test, never a caller.";
	}

	const detector =
		evidence.detector && evidence.score !== null
			? `${evidence.detector} scored ${evidence.score.toFixed(2)}`
			: (evidence.detector ?? "an unnamed detector");

	const speech = evidence.speechMs === null ? "" : ` on ${evidence.speechMs}ms of speech`;

	return evidence.agentWasPlaying
		? `Cut ${evidence.playbackMs ?? 0}ms into the reply: ${detector}${speech}.`
		: `Cancelled before any audio played, so the caller heard nothing: ${detector}${speech}.`;
}

/** The vendors that actually served a turn — a mid-call failover can differ from the agent's config. */
export function laneOf(turn: CallDumpTurn) {
	return [
		{ leg: "Recognition", provider: turn.sttProvider, model: turn.sttModel },
		{ leg: "Inference", provider: turn.llmProvider, model: turn.llmModel },
		{
			leg: "Synthesis",
			provider: turn.ttsProvider,
			// The voice matters more than the model for diagnosing silence, and
			// it is the field the ladder rewrites.
			model: turn.ttsVoiceId ?? turn.ttsModel,
		},
	].filter((entry) => entry.provider !== null);
}

/**
 * The stages, in the order a turn passes through them, grouped by WHO PAID.
 *
 * ## The first three are measured on the CALLER's turn, not the agent's
 *
 * A conversation is two rows and the first half of a reply's latency happens
 * on the other one: an agent turn OPENS at the moment the transcript commits,
 * so endpointing read off it could only ever be zero, and `sttFinalAt` is
 * stamped while the caller's turn is still the open one. `caller` is the turn
 * immediately before this one, when there is one; an agent turn that opens a
 * call has none and honestly reports all three as unknown.
 *
 * ## `owner` exists because the biggest stage here is not a vendor's
 *
 * `speechEndAt → sttFinalAt` is `UtteranceBoundary`'s hold: the runtime stamps
 * `speech_end` the instant Silero reports silence, then holds the vendor's
 * final text for the agent's utterance hold before committing it. On a clean
 * turn the figure reads back the configured value exactly. Labelling our own
 * dial as a vendor's work sends a reader shopping for a faster STT provider
 * when the largest term is an environment variable — so every row is either
 * `ours` or names the vendor that served it, never a mix.
 *
 * ## Which stages add up to `responseLatencyMs`
 *
 * It is `speechEnd → ttsFirstByte`. Exactly the four `silence` stages span it
 * and sum to it. Endpoint detection ends AT `speechEnd`, so it sits before the
 * clock starts; the rest run behind playback.
 */
export function stagesOf(turn: CallDumpTurn, caller: CallDumpTurn | null) {
	return [
		{
			label: "Endpoint detection",
			group: "before" as const,
			owner: "ours" as const,
			// `lastPartialAt`, not `startedAt`. Interims stop when the caller
			// stops, so the gap to silence being declared is the closest view of
			// the detector's own decision.
			value: span(caller?.lastPartialAt ?? null, caller?.speechEndAt ?? null),
			reversed: stageOrderViolated(caller?.lastPartialAt ?? null, caller?.speechEndAt ?? null),
			hint: "The STT vendor's last interim → Silero declaring silence. Not a configured threshold: it carries the vendor's interim lag as well as the detector's, which is why it swings.",
		},
		{
			label: "Utterance hold",
			group: "silence" as const,
			owner: "ours" as const,
			value: span(caller?.speechEndAt ?? null, caller?.sttFinalAt ?? null),
			reversed: stageOrderViolated(caller?.speechEndAt ?? null, caller?.sttFinalAt ?? null),
			hint: "The agent's utterance hold. After silence is declared we hold the transcript for a further fixed quiet before committing it, so a caller pausing mid-sentence is not answered over. On a clean turn this figure IS that setting — no vendor work is in it.",
		},
		{
			label: "Dispatch",
			group: "silence" as const,
			owner: "ours" as const,
			value: span(caller?.sttFinalAt ?? null, turn.inferenceStartedAt),
			reversed: stageOrderViolated(caller?.sttFinalAt ?? null, turn.inferenceStartedAt),
			hint: "Our own handover: transcript in hand → prompt sent.",
		},
		{
			label: "Inference (first token)",
			group: "silence" as const,
			owner: turn.llmProvider,
			value: span(turn.inferenceStartedAt, turn.llmFirstTokenAt),
			reversed: stageOrderViolated(turn.inferenceStartedAt, turn.llmFirstTokenAt),
			hint: "The last token barely affects perceived latency; the first one is everything.",
		},
		{
			label: "Synthesis (first byte)",
			group: "silence" as const,
			owner: turn.ttsProvider,
			value: span(turn.llmFirstTokenAt, turn.ttsFirstByteAt),
			reversed: stageOrderViolated(turn.llmFirstTokenAt, turn.ttsFirstByteAt),
			hint: "First token → first audio byte. Streaming overlaps this with generation.",
		},
		{
			label: "Inference (complete)",
			group: "behind" as const,
			owner: turn.llmProvider,
			value: span(turn.llmFirstTokenAt, turn.llmDoneAt),
			reversed: stageOrderViolated(turn.llmFirstTokenAt, turn.llmDoneAt),
			hint: "Runs behind playback, so it is only a problem when it outlasts it.",
		},
		{
			label: "Synthesis (complete)",
			group: "behind" as const,
			owner: turn.ttsProvider,
			value: span(turn.ttsFirstByteAt, turn.ttsDoneAt),
			reversed: stageOrderViolated(turn.ttsFirstByteAt, turn.ttsDoneAt),
			hint: "Also behind playback.",
		},
		{
			// The only row measured at the CALLER. Every other stage is stamped
			// inside the runtime, before the audio reaches the carrier — which
			// is why a call the caller heard break up can show every stage in
			// budget. Reported by the carrier, not a span.
			label: "Carrier backlog",
			group: "behind" as const,
			owner: "carrier" as const,
			value: turn.carrierBacklogMs,
			reversed: false,
			hint: "How far behind the carrier was when this turn ended — audio it had accepted and not yet played. Past a second or so the caller hears the agent break up mid-word, because the jitter buffer runs dry. Blank on browser sessions, which have no carrier.",
		},
	];
}

export type Stage = ReturnType<typeof stagesOf>[number];

/**
 * How much of the caller's wait we chose, and how much a vendor spent.
 *
 * "Which step do we optimise" has a different answer depending on which side
 * is larger, and on real traffic it is routinely "ours". Only the four stages
 * that span `responseLatencyMs` are counted — adding the ones before or behind
 * it would produce a total that matches nothing.
 */
export function attribution(stages: Stage[]) {
	const inTotal = stages.filter((stage) => stage.group === "silence");

	const sum = (owned: boolean) =>
		inTotal
			.filter((stage) => (stage.owner === "ours") === owned)
			.reduce<number | null>(
				(total, stage) => (stage.value === null ? total : (total ?? 0) + stage.value),
				null,
			);

	return { ours: sum(true), vendors: sum(false) };
}

export const GROUP_LABELS: Record<Stage["group"], { title: string; note: string }> = {
	before: {
		title: "Before the clock starts",
		note: "This happens before the TTFB is measured from, so it is not part of it — but the caller sat through it too.",
	},
	silence: {
		title: "The silence the caller sat in",
		note: "These four span the TTFB and sum to it.",
	},
	behind: {
		title: "Behind playback",
		note: "Overlapped with the reply being spoken. Only a problem when one outlasts it.",
	},
};
